import { inject, observer } from "mobx-react";
import * as moment from "moment";
import * as React from "react";
import { UncontrolledTooltip } from "reactstrap";
import { Link, RouteComponentProps, RouteProps, withRouter } from "react-router-dom";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";
import { HorseForSaleEndpoints } from "src/endpoints/HorseForSaleEndpoints";
import { CreditApplication, Customer, ExtractFriendlyName, InsuranceRecord, SimpleCustomer } from "src/model/Customer";
import { InsuranceRecordRequest } from "src/requests/CustomerRequests";
import { BuyerRequest, HorseForSalePurchaseRequest } from "src/requests/HorseForSaleRequests";
import { ConfigurationStore } from "src/stores/ConfigurationStore";

import { faCheck, faRedo, faShoppingCart, faExternalLinkSquareAlt, faFlag } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { HammerFallOutcome, HorseForSale, HorseForSalePurchase, HSTExemptionType, SimpleHorseSale } from "../../model/HorseForSale";
import { HorseForSaleListStore } from "../../stores/HorseForSaleListStore";
import { HorseForSaleStore } from "../../stores/HorseForSaleStore";
import { CustomerLink } from "../common/CustomerLink";
import LoadingWidget from "../common/LoadingWidget";
import { SimpleAlert, SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { errorMessage } from "../common/Toast";
import CheckoutBuyers from "./CheckoutBuyers";
import CheckoutCreditAndInsurance from "./CheckoutCreditAndInsurance";
import CheckoutHSTExemption from "./CheckoutHSTExemption";
import CheckoutInvoiceAndPayment from "./CheckoutInvoiceAndPayment";
import CheckoutLink from "./CheckoutLink";
import CheckoutStableRelease from "./CheckoutStableRelease";

export namespace EditableHorseForSalePurchase {
    export interface Fields {
        buyers: Array<PurchaseFields>;

        // Only one or the other, or none
        winningBidderCreditApplication: CreditApplication | null;
        winningBidderOnTheSpotCreditApplication: {
            notes: string;
        } | null;
        // Only specified iff a credit application exists
        winningBidderInsuranceRecord: InsuranceRecord | null;
    }

    export interface HSTExemptionFields {
        exemption: HSTExemptionType;
        notes: string;
        firstNationsIdNumber?: string | null;
    }

    export interface PurchaseFields {
        id: number | null;
        purchaser: Customer | null;
        percentage: string;
        coveredByWinningBidderCreditAndInsurance: boolean;
        trainer: boolean;
        notes: string | null;

        // Only one or the other, or none
        creditApplication: CreditApplication | null;
        onTheSpotCreditApplication: {
            notes: string;
        } | null;
        // Only specified iff a credit application exists
        insuranceRecord: InsuranceRecord | null;

        hstExemption: HSTExemptionFields;
    }

    export const PurchaseMakeCopy = (buyer: PurchaseFields): PurchaseFields => {
        return {
            id: buyer.id,
            purchaser: buyer.purchaser,
            percentage: buyer.percentage,
            coveredByWinningBidderCreditAndInsurance: buyer.coveredByWinningBidderCreditAndInsurance,
            trainer: buyer.trainer,
            notes: buyer.notes,
            hstExemption: buyer.hstExemption,
            creditApplication: buyer.creditApplication,
            onTheSpotCreditApplication: buyer.onTheSpotCreditApplication,
            insuranceRecord: buyer.insuranceRecord
        };
    };

    export const MakeCopy = (fields: Fields): Fields => {
        return {
            buyers: fields.buyers.map(buyer => PurchaseMakeCopy(buyer)),
            winningBidderCreditApplication: fields.winningBidderCreditApplication,
            winningBidderOnTheSpotCreditApplication: fields.winningBidderOnTheSpotCreditApplication,
            winningBidderInsuranceRecord: fields.winningBidderInsuranceRecord
        };
    };

    export const EmptyHSTExemption = (): HSTExemptionFields => {
        return {
            exemption: HSTExemptionType.NONE,
            notes: ""
        };
    };

    export const EmptyPurchaser = (): PurchaseFields => {
        return {
            id: null,
            purchaser: null,
            percentage: "",
            coveredByWinningBidderCreditAndInsurance: false,
            trainer: false,
            notes: null,

            hstExemption: EmptyHSTExemption(),
            creditApplication: null,
            onTheSpotCreditApplication: null,
            insuranceRecord: null
        };
    };

    export const Empty = (): Fields => {
        return {
            buyers: [],
            winningBidderCreditApplication: null,
            winningBidderOnTheSpotCreditApplication: null,
            winningBidderInsuranceRecord: null
        };
    };

    export const PurchaseFromModel = (m: HorseForSalePurchase | PurchaseFields): PurchaseFields => {
        if (!m.purchaser || !m.percentage) {
            // Internal error, this should never happen
            throw new Error("Cannot call ToRequest with null purchaser and/or percentage");
        }

        return {
            id: null,
            //Step 1
            purchaser: m.purchaser,
            percentage: m.percentage + "",
            coveredByWinningBidderCreditAndInsurance: m.coveredByWinningBidderCreditAndInsurance,
            trainer: m.trainer,
            notes: m.notes || "",
            //Step 2
            creditApplication: m.creditApplication && m.creditApplication.type === "PERMANENT" ? m.creditApplication : null,
            onTheSpotCreditApplication:
                m.creditApplication && m.creditApplication.type === "ON_THE_SPOT" ? { notes: m.creditApplication.notes || "" } : null,
            insuranceRecord: m.insuranceRecord,
            //Step 3
            hstExemption: { exemption: m.hstExemption.exemption, notes: m.hstExemption.notes || "" }
        };
    };

    export const FromModel = (m: HammerFallOutcome): Fields => {
        return {
            buyers: m.purchasers.map(p => PurchaseFromModel(p)),
            winningBidderCreditApplication:
                m.winningBidderCreditApplication && m.winningBidderCreditApplication.type === "PERMANENT" ? m.winningBidderCreditApplication : null,
            winningBidderOnTheSpotCreditApplication:
                m.winningBidderCreditApplication && m.winningBidderCreditApplication.type === "ON_THE_SPOT"
                    ? { notes: m.winningBidderCreditApplication.notes || "" }
                    : null,
            winningBidderInsuranceRecord: m.winningBidderInsuranceRecord
        };
    };

    export const PurchaseToRequest = (fields: PurchaseFields): BuyerRequest => {
        if (!fields.purchaser || !fields.percentage || !fields.hstExemption) {
            // Internal error, this should never happen
            throw new Error("Cannot call ToRequest with null purchaser and/or percentage");
        }
        return {
            // Step 1
            buyerId: fields.purchaser.id,
            percentage: parseFloat("" + (fields.percentage || 0)),
            coveredByWinningBidderCreditAndInsurance: fields.coveredByWinningBidderCreditAndInsurance,
            trainer: fields.trainer,
            notes: fields.notes || "",

            // Step 2
            creditApplicationId: fields.creditApplication ? fields.creditApplication.id : null,
            onTheSpotCreditApplication: fields.onTheSpotCreditApplication ? fields.onTheSpotCreditApplication : null,
            insuranceRecordId: fields.insuranceRecord ? fields.insuranceRecord.id : null,

            // Step 3
            hstExemption: fields.hstExemption
        };
    };

    export const ToRequest = (fields: Fields): HorseForSalePurchaseRequest => {
        return {
            buyers: fields.buyers.map(b => PurchaseToRequest(b)),
            winningBidderCreditApplicationId: fields.winningBidderCreditApplication ? fields.winningBidderCreditApplication.id : null,
            winningBidderOnTheSpotCreditApplication: fields.winningBidderOnTheSpotCreditApplication
                ? fields.winningBidderOnTheSpotCreditApplication
                : null,
            winningBidderInsuranceRecordId: fields.winningBidderInsuranceRecord ? fields.winningBidderInsuranceRecord.id : null
        };
    };
}

export interface Props extends RouteComponentProps<any> {
    configurationStore?: ConfigurationStore;
    horseForSaleStore?: HorseForSaleStore;
    horseForSaleEndpoints?: HorseForSaleEndpoints;
    customerEndpoints?: CustomerEndpoints;

    currentTab: string;
    horseForSaleIds: Array<number>;
}

export interface State {
    winningBidder: Customer | null; // Winning Bidder
    currentHorseForSale: HorseForSale | null; //this.props.location.state
    horseSale: SimpleHorseSale | null;
    horseForSaleList: Array<HorseForSale>; //Imported Horses to Checkout
    horseForSaleRequests: { [horseForSaleId: number]: EditableHorseForSalePurchase.Fields };
    horseForSaleCheckList: Array<number>;
    loading: boolean;
    progressedThroughInvoices: boolean;
}

export enum CheckoutTabs {
    BUYERS = "buyers",
    CREDIT_AND_INSURANCE = "credit-and-insurance",
    HST_EXEMPTION = "hst-exemption",
    INVOICE_AND_PAYMENT = "invoice-and-payment",
    STABLE_RELEASE = "stable-release"
}

const DefaultState = (): State => {
    return {
        currentHorseForSale: null,
        horseSale: null,
        horseForSaleList: [],
        winningBidder: null,
        horseForSaleCheckList: [],
        horseForSaleRequests: {},
        loading: false,
        progressedThroughInvoices: false
    };
};

@inject("configurationStore")
@inject("horseForSaleStore")
@inject("horseForSaleEndpoints")
@inject("customerEndpoints")
@observer
export class CheckoutTemplate extends React.Component<Props, State> {
    state: State = DefaultState();

    submitHorseForSaleCheckout = async () => {
        const currentHorseForSaleId = this.state.currentHorseForSale!.id;
        const currentHorseForSaleRequests = this.state.horseForSaleRequests[currentHorseForSaleId];
        try {
            const request = EditableHorseForSalePurchase.ToRequest(currentHorseForSaleRequests);
            this.setState({ loading: true });

            const hfs = await this.props.horseForSaleEndpoints!.purchaseHorse(currentHorseForSaleId, request);
            this.setState({
                horseForSaleCheckList: [...this.state.horseForSaleCheckList, currentHorseForSaleId]
                // KB Oct 12 2019: Commented out since it's probably easier and safer to refresh
                //      the whole thing (to make sure all invoices for all customers are up to date too)
                // horseForSaleList: [ ...this.state.horseForSaleList.filter(h => h.id !== hfs.id), hfs ],
                // horseForSaleRequests: {
                //     ...this.state.horseForSaleRequests,
                //     [ hfs.id ]: EditableHorseForSalePurchase.FromModel(hfs.outcome as HammerFallOutcome)
                // }
            });
            await this.refreshCheckout();
            this.goToNextHorse();
        } catch (error) {
            errorMessage(error);
        } finally {
            this.setState({ loading: false });
        }
    };

    getHorseForSaleList = (horseForSaleIdList: Array<number>): Promise<Array<HorseForSale>> => {
        const { customerEndpoints } = this.props;
        if (!customerEndpoints) {
            throw new Error(" ** No Customer Store **");
        }

        const { horseForSaleEndpoints } = this.props;
        if (!horseForSaleEndpoints) {
            throw new Error(" ** No Customer Store **");
        }

        try {
            const horsesForSaleListPromise = Promise.all(horseForSaleIdList.map(async id => await horseForSaleEndpoints.getHorseForSale(id)));
            return horsesForSaleListPromise;
        } catch (error) {
            throw error;
        }
    };

    get linkPrefix() {
        return `/checkout/${this.props.horseForSaleIds.sort().join("+")}`;
    }

    goToTab = (tab: string) => {
        this.props.history!.push(`${this.linkPrefix}/${tab}`);
    };

    completeCheckout = () => {
        this.props.history!.push("/customers");
    };

    defaultCurrentHorseForSale = (horseForSaleList: Array<HorseForSale> = this.state.horseForSaleList): HorseForSale | null => {
        if (this.props.currentTab === CheckoutTabs.INVOICE_AND_PAYMENT || this.props.currentTab === CheckoutTabs.STABLE_RELEASE) {
            return null;
        } else {
            return horseForSaleList[0] || null;
        }
    };

    goToNextHorse = () => {
        // This shouldn't happen, but just in case:
        if (this.state.currentHorseForSale === null) {
            console.warn("in goToNextHorse with a null current horse for sale?");
            this.props.history!.push(`${this.linkPrefix}/${CheckoutTabs.INVOICE_AND_PAYMENT}`);
            return;
        }

        const horseForSale = this.state
            .horseForSaleList!.sort((a, b) => (a.hipNumber || 0) - (b.hipNumber || 0))
            .find(hfs => hfs.hipNumber! > this.state.currentHorseForSale!.hipNumber!);
        if (horseForSale) {
            this.props.history!.push(`${this.linkPrefix}/${CheckoutTabs.BUYERS}`);
            this.setState({ currentHorseForSale: horseForSale });
        } else {
            this.props.history!.push(`${this.linkPrefix}/${CheckoutTabs.INVOICE_AND_PAYMENT}`);
            this.setState({ currentHorseForSale: null });
        }
    };

    goBackHorse = () => {
        const horseForSale =
            this.state.currentHorseForSale === null
                ? this.state.horseForSaleList![this.state.horseForSaleList!.length - 1]
                : this.state
                      .horseForSaleList!.sort((a, b) => (b.hipNumber || 0) - (a.hipNumber || 0))
                      .find(hfs => (hfs.hipNumber || 0) < (this.state.currentHorseForSale!.hipNumber || 0));
        if (horseForSale) {
            this.setState({ currentHorseForSale: horseForSale });
            this.props.history!.push(`${this.linkPrefix}/${CheckoutTabs.HST_EXEMPTION}`);
        }
    };

    onChange = (horseForSale: HorseForSale, updatedFields: EditableHorseForSalePurchase.Fields) => {
        const { horseForSaleRequests } = this.state;
        horseForSaleRequests[horseForSale.id] = updatedFields;
        this.setState({ horseForSaleRequests });
    };

    componentDidMount() {
        this.refreshCheckout();
    }

    componentDidUpdate(prevState: Props, prevProps: State) {
        // Clear the current horse for sale if we're no longer in Steps 1-3
        if (
            (this.props.currentTab === CheckoutTabs.INVOICE_AND_PAYMENT || this.props.currentTab === CheckoutTabs.STABLE_RELEASE) &&
            this.state.currentHorseForSale
        ) {
            this.setState({ currentHorseForSale: null });
        }
    }

    refreshCheckout = async () => {
        this.setState({ loading: true });
        try {
            // At least one horse for sale must be specified
            if (this.props.horseForSaleIds.length === 0) {
                throw new Error("At least one horse for sale ID is required");
            }

            // Download all horses for sale
            const horseForSaleList = (await this.getHorseForSaleList(this.props.horseForSaleIds)).sort(
                (a: HorseForSale, b: HorseForSale) => a.hipNumber! - b.hipNumber!
            );

            // Make sure all horses for sale have a HAMMER_FALL outcome
            horseForSaleList.forEach(hfs => {
                if (!hfs.outcome || hfs.outcome.type !== "HAMMER_FALL") {
                    throw new Error("All horses for sale must have a 'hammer fall' outcome present.");
                }
            });

            // Make sure they all have the same winning bidder
            const winningBidderIds = horseForSaleList.map(hfs => (hfs.outcome! as HammerFallOutcome).winningBidder.id);
            if (new Set([...winningBidderIds]).size !== 1) {
                throw new Error("Winning bidder for all selected horses for sale must be the same");
            }
            //await this.props.customerEndpoints!.getCustomer(winningBidderIds[0]);
            const winningBidder = (horseForSaleList[0].outcome as HammerFallOutcome).winningBidder;

            if (new Set([...horseForSaleList.map(hfs => hfs.sale.id)]).size !== 1) {
                throw new Error("The sale for all selected horses for sale must be the same.");
            }
            const horseSale = horseForSaleList[0].sale;

            const horseForSaleRequests = horseForSaleList.reduce((obj, hfs) => {
                return {
                    ...obj,
                    [hfs.id]: EditableHorseForSalePurchase.FromModel(hfs.outcome! as HammerFallOutcome)
                };
            }, {});

            this.setState({
                horseForSaleList,
                currentHorseForSale:
                    this.state.currentHorseForSale === null ? this.defaultCurrentHorseForSale(horseForSaleList) : this.state.currentHorseForSale,
                winningBidder,
                horseForSaleRequests,
                horseSale,
                // Assume that we've progressed through the invoices if all stable releases have been granted
                progressedThroughInvoices: Object.values(horseForSaleList)
                    .map(hfs => (hfs.outcome as HammerFallOutcome).stableReleaseProvidedTo)
                    .every(val => val !== null)
            });
        } catch (e) {
            this.showError(e);
        } finally {
            this.setState({ loading: false });
        }
    };

    horseForSaleUpdated = (horseForSale: HorseForSale) => {
        const filteredHorsesForSale = this.state.horseForSaleList.filter(hfs => hfs.id !== horseForSale.id);
        const newHorseForSaleList = [horseForSale, ...filteredHorsesForSale].sort((a: HorseForSale, b: HorseForSale) => a.hipNumber! - b.hipNumber!);
        this.setState({ horseForSaleList: newHorseForSaleList });
    };

    customerUpdated = (customer: Customer) => {
        // This is hacky....but allows child components a means to publish any updates
        // to clients so they don't have to worry about updating it in multiple places.
        // E.g., when a new insurance record is added.
        const obj: any = {
            winningBidder: this.state.winningBidder && customer.id === this.state.winningBidder.id ? customer : this.state.winningBidder,
            horseForSaleRequests: JSON.parse(JSON.stringify(this.state.horseForSaleRequests)),
            horseForSaleList: JSON.parse(JSON.stringify(this.state.horseForSaleList))
        };
        // Update any usages of the customer in the horse for sale requests
        Object.values(obj.horseForSaleRequests).forEach((requests: EditableHorseForSalePurchase.Fields) => {
            requests.buyers.forEach(buyer => {
                if (buyer.purchaser && buyer.purchaser.id === customer.id) {
                    buyer.purchaser = customer;
                }
            });
        });
        // Update any usages of the customer in the source horse for sale list
        Object.values(obj.horseForSaleList).forEach((horseForSale: HorseForSale) => {
            if (horseForSale.outcome && (horseForSale.outcome as HammerFallOutcome).purchasers) {
                (horseForSale.outcome as HammerFallOutcome).purchasers.forEach(purchase => {
                    if (purchase.purchaser && purchase.purchaser.id === customer.id) {
                        purchase.purchaser = customer;
                    }
                    // Also update the buyer invoice if present within the updated cusomer's account
                    if (purchase.buyerInvoice && customer.account.account.invoices.some(i => i.id === purchase.buyerInvoice!.id)) {
                        purchase.buyerInvoice = customer.account.account.invoices.find(i => i.id === purchase.buyerInvoice!.id)!;
                    }
                });
            }
        });
        this.setState(obj);
        this.forceUpdate();
    };

    showError(e: any) {
        SimpleAlert("An error occurred while loading the checkout. Press back and try again.");
        console.error("Error while loading checkout", e);
    }

    render() {
        if (this.state.loading) {
            return (
                <div style={{ width: "100%", textAlign: "center", marginTop: "30%", background: "#fff" }}>
                    <LoadingWidget />
                </div>
            );
        }
        if (this.state.horseForSaleList.length === 0) {
            return <div />;
        }

        const currentHorseForSale = this.state.currentHorseForSale;
        const results = this.state.horseForSaleList;

        const showGoBackHorse = currentHorseForSale === null || this.state.horseForSaleList[0].id !== currentHorseForSale.id;
        const showGoToNextHorse =
            currentHorseForSale !== null && this.state.horseForSaleList[this.state.horseForSaleList.length - 1].id !== currentHorseForSale.id;

        const horseSale = this.state.horseSale;
        if (!horseSale) {
            console.warn("Couldn't find HorseSale?");
            return <div />;
        }

        const allCheckedOut = Object.values(this.state.horseForSaleRequests)
            .map(hfsr => hfsr.buyers.reduce((amount, hfsp) => amount + parseInt(hfsp.percentage!, 10), 0))
            .every(val => val === 100);
        const allReleased = Object.values(this.state.horseForSaleList)
            .map(hfs => (hfs.outcome as HammerFallOutcome).stableReleaseProvidedTo)
            .every(val => val !== null);

        return (
            <div>
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item active" aria-current="page">
                            Checkout
                        </li>
                    </ol>
                </nav>
                <div className="content">
                    <div className="row">
                        <div className="col-8">
                            <h1>
                                <span>
                                    <FontAwesomeIcon icon={faShoppingCart} size="2x" className="mr-2 color-info" />
                                </span>
                                Checkout
                            </h1>
                        </div>
                        <div className="col-4 text-right">
                            {/* <button type="button" className="btn btn-light shadow" onClick={() => this.refreshCheckout()}>
                                <FontAwesomeIcon icon={faRedo} className="mr-2" />
                                Refresh Checkout
                            </button> */}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3 col-lg-4 col-xl-3">
                            <div className="card shadow">
                                <div className="card-header">Buyer(s), Credit and Insurance &amp; HST Exemption</div>
                                <nav className="flex-column  intern-navigation">
                                    <ul className="checkout-menu">
                                        {results &&
                                            results
                                                .sort((a, b) => a.hipNumber! - b.hipNumber!)
                                                .map((hfs: HorseForSale) => {
                                                    const match = currentHorseForSale ? hfs.hipNumber === currentHorseForSale.hipNumber : false;
                                                    const currentHorseForSaleRequests = this.state.horseForSaleRequests[hfs.id];
                                                    const isNotOneHundredPercent =
                                                        currentHorseForSaleRequests.buyers.reduce(
                                                            (amount, hfsp) => amount + parseInt(hfsp.percentage!, 10),
                                                            0
                                                        ) !== 100;
                                                    return (
                                                        <li key={hfs.id}>
                                                            <button
                                                                className={`checkout-nav-link ${match ? `active` : ``}`}
                                                                onClick={() => {
                                                                    this.setState({ currentHorseForSale: hfs });
                                                                    this.props.history!.push(`${this.linkPrefix}/${CheckoutTabs.BUYERS}`);
                                                                }}
                                                            >
                                                                #{hfs.hipNumber} {hfs.horse.name}
                                                                {hfs.state === "SOLD" && (
                                                                    <FontAwesomeIcon
                                                                        icon={faCheck}
                                                                        className={
                                                                            this.state.horseForSaleCheckList.find(
                                                                                horseForSaleId => horseForSaleId === hfs.id
                                                                            )
                                                                                ? "text-success"
                                                                                : "text-secondary"
                                                                        }
                                                                        style={
                                                                            match ||
                                                                            this.state.horseForSaleCheckList.find(
                                                                                horseForSaleId => horseForSaleId === hfs.id
                                                                            )
                                                                                ? { opacity: 1 }
                                                                                : { opacity: 0.6 }
                                                                        }
                                                                    />
                                                                )}
                                                            </button>
                                                            {match &&
                                                                this.props.currentTab !== CheckoutTabs.INVOICE_AND_PAYMENT &&
                                                                this.props.currentTab !== CheckoutTabs.STABLE_RELEASE && (
                                                                    <ul className="checkout-submenu">
                                                                        <CheckoutLink
                                                                            label="1 | Buyer(s)"
                                                                            horseForSaleIds={this.props.horseForSaleIds}
                                                                            page={CheckoutTabs.BUYERS}
                                                                            active={this.props.currentTab === CheckoutTabs.BUYERS}
                                                                            onClick={() => this.setState({ currentHorseForSale: hfs })}
                                                                            icon={false}
                                                                        />
                                                                        <CheckoutLink
                                                                            label="2 | Credit and Insurance"
                                                                            horseForSaleIds={this.props.horseForSaleIds}
                                                                            page={CheckoutTabs.CREDIT_AND_INSURANCE}
                                                                            active={this.props.currentTab === CheckoutTabs.CREDIT_AND_INSURANCE}
                                                                            onClick={() => this.setState({ currentHorseForSale: hfs })}
                                                                            disabled={match && isNotOneHundredPercent}
                                                                            icon={false}
                                                                        />
                                                                        <CheckoutLink
                                                                            label="3 | HST Exemption"
                                                                            horseForSaleIds={this.props.horseForSaleIds}
                                                                            page={CheckoutTabs.HST_EXEMPTION}
                                                                            active={this.props.currentTab === CheckoutTabs.HST_EXEMPTION}
                                                                            onClick={() => this.setState({ currentHorseForSale: hfs })}
                                                                            disabled={match && isNotOneHundredPercent}
                                                                            icon={false}
                                                                        />
                                                                    </ul>
                                                                )}
                                                        </li>
                                                    );
                                                })}
                                    </ul>
                                </nav>
                            </div>
                            <div className="card shadow mt-3">
                                <div className="card-header">Invoice and Payment &amp; Stable Release</div>
                                <nav className="flex-column  intern-navigation">
                                    <ul className="checkout-menu">
                                        <CheckoutLink
                                            label="4 | Invoice &amp; Payment"
                                            horseForSaleIds={this.props.horseForSaleIds}
                                            page={CheckoutTabs.INVOICE_AND_PAYMENT}
                                            active={this.props.currentTab === CheckoutTabs.INVOICE_AND_PAYMENT}
                                            style="checkout-nav-link"
                                            disabled={!allCheckedOut}
                                            icon={
                                                allCheckedOut &&
                                                this.state.progressedThroughInvoices && (
                                                    <FontAwesomeIcon
                                                        icon={faCheck}
                                                        size="sm"
                                                        className={`link-icon ml-auto my-auto`}
                                                        style={{ fontSize: ".7rem", fontWeight: 400 }}
                                                    />
                                                )
                                            }
                                        />
                                        {/* <CheckoutLink
                                            label="5 | Stable Release"
                                            horseForSaleIds={this.props.horseForSaleIds}
                                            page={CheckoutTabs.STABLE_RELEASE}
                                            active={this.props.currentTab === CheckoutTabs.STABLE_RELEASE}
                                            style="checkout-nav-link"
                                            disabled={!allCheckedOut || !this.state.progressedThroughInvoices}
                                            icon={
                                                allCheckedOut &&
                                                allReleased && (
                                                    <FontAwesomeIcon
                                                        icon={faCheck}
                                                        size="sm"
                                                        className={`link-icon ml-auto my-auto`}
                                                        style={{ fontSize: ".7rem", fontWeight: 400 }}
                                                    />
                                                )
                                            }
                                        /> */}
                                    </ul>
                                </nav>
                            </div>
                        </div>
                        <div className="col-md-9 col-lg-8 col-xl-9">
                            <div className="card shadow-sm mb-3">
                                <div className="card-header editable-card-header">
                                    <div className="row mx-0">
                                        <div className="col-6 px-0 d-flex">
                                            Winning Bidder:
                                            {/* <CustomerLink customer={this.state.winningBidder} targetBlank={true} /> */}
                                            <Link
                                                id={`winningBidder_top_` + this.state.winningBidder!.id}
                                                to={`/customer/${this.state.winningBidder!.id}/overview`}
                                                className="d-flex ml-2 justify-content-start align-items-center flex-nowrap text-info"
                                                target="_blank"
                                            >
                                                <div
                                                    className={this.state.winningBidder!.dateFlagged ? `text-danger` : ``}
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    {ExtractFriendlyName(this.state.winningBidder!)}
                                                </div>
                                                <FontAwesomeIcon icon={faExternalLinkSquareAlt} className="ml-1" />
                                            </Link>
                                            {this.state.winningBidder!.dateFlagged && (
                                                <UncontrolledTooltip
                                                    placement="top"
                                                    target={`winningBidder_top_` + this.state.winningBidder!.id}
                                                    delay={0}
                                                    className="tooltipstrap"
                                                >
                                                    <FontAwesomeIcon icon={faFlag} className="mr-2 fa-sm" style={{ color: "FFB62E" }} />
                                                    {this.state.winningBidder!.flaggedNotes || "-"}
                                                </UncontrolledTooltip>
                                            )}
                                        </div>
                                        {currentHorseForSale && (
                                            <div className="col-6 px-0 text-right">
                                                Sale Date: {moment(currentHorseForSale.saleDate!.date).format("MMM DD, YYYY")}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {this.props.currentTab === CheckoutTabs.BUYERS && currentHorseForSale && (
                                <CheckoutBuyers
                                    goBackHorse={() => this.goBackHorse()}
                                    goToTab={tab => this.goToTab(tab)}
                                    winningBidder={this.state.winningBidder!}
                                    currentHorseForSale={currentHorseForSale!}
                                    showGoBackHorse={showGoBackHorse}
                                    horseForSalePurchaseList={this.state.horseForSaleRequests[currentHorseForSale.id]}
                                    onChange={(horseForSale, updatedFields) => this.onChange(horseForSale, updatedFields)}
                                />
                            )}
                            {this.props.currentTab === CheckoutTabs.CREDIT_AND_INSURANCE && currentHorseForSale && (
                                <CheckoutCreditAndInsurance
                                    goToTab={tab => this.goToTab(tab)}
                                    winningBidder={this.state.winningBidder!}
                                    currentHorseForSale={currentHorseForSale!}
                                    horseForSalePurchaseList={this.state.horseForSaleRequests[currentHorseForSale.id]}
                                    onChange={this.onChange}
                                    onCustomerUpdated={this.customerUpdated}
                                />
                            )}
                            {this.props.currentTab === CheckoutTabs.HST_EXEMPTION && currentHorseForSale && (
                                <CheckoutHSTExemption
                                    nextHorse={() => this.goToNextHorse()}
                                    goToTab={tab => this.goToTab(tab)}
                                    showGoBackHorse={showGoBackHorse}
                                    showGoToNextHorse={showGoToNextHorse}
                                    winningBidder={this.state.winningBidder!}
                                    currentHorseForSale={currentHorseForSale!}
                                    horseForSalePurchaseList={this.state.horseForSaleRequests[currentHorseForSale.id]}
                                    onChange={this.onChange}
                                    submitHorseForSaleCheckout={() => this.submitHorseForSaleCheckout()}
                                />
                            )}
                            {this.props.currentTab === CheckoutTabs.INVOICE_AND_PAYMENT && (
                                <CheckoutInvoiceAndPayment
                                    onBackward={() => this.goBackHorse()}
                                    onForward={() => {
                                        this.setState({ progressedThroughInvoices: true });
                                        this.completeCheckout();
                                        // this.goToTab(CheckoutTabs.STABLE_RELEASE);
                                    }}
                                    horseForSaleList={this.state.horseForSaleList}
                                    horseForSaleRequests={this.state.horseForSaleRequests}
                                    horseSale={horseSale}
                                    onCustomerUpdated={this.customerUpdated}
                                    winningBidder={this.state.winningBidder!}
                                />
                            )}
                            {/* {this.props.currentTab === CheckoutTabs.STABLE_RELEASE && this.state.progressedThroughInvoices && (
                                <CheckoutStableRelease
                                    goToTab={tab => this.goToTab(tab)}
                                    horseForSaleList={this.state.horseForSaleList}
                                    horseForSaleRequests={this.state.horseForSaleRequests}
                                    winningBidder={this.state.winningBidder!}
                                    onHorseForSaleUpdated={this.horseForSaleUpdated}
                                    onCompleteCheckout={this.completeCheckout}
                                />
                            )} */}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default withRouter<Props>(CheckoutTemplate);



// WEBPACK FOOTER //
// ./src/components/checkout/CheckoutTemplate.tsx